import { useEffect } from "react";
import { Link } from "react-router-dom";

function Page({ title, children }) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${title} | Oracle Rclone`;
    return () => { document.title = previousTitle; };
  }, [title]);

  return (
    <main className="container-fluid" lang="en" dir="ltr">
      <article className="about-main entry-content">
        <h1 className="main-title">{title}</h1>
        {children}
      </article>
    </main>
  );
}

export function OracleRclone() {
  return (
    <Page title="Oracle Rclone">
      <p>
        Oracle Rclone is a private, personal-use OAuth application maintained by
        Yoseph Aljohani to connect rclone to the owner's Google Drive for encrypted
        file backups and synchronization. It is not offered as a public service.
      </p>
      <h2 className="small-title">How it works</h2>
      <p>
        The owner authorizes rclone through Google's consent screen. Rclone uses
        the granted Drive permissions to list, upload, download, update, or delete
        files as needed for the owner's commands and the selected OAuth scope.
        Authorization happens through rclone, not on this website.
      </p>
      <p>
        Encryption is provided by a configured rclone crypt remote layered over
        Google Drive. Files sent through that remote are encrypted before upload
        and decrypted on the owner's machine when read. Filename encryption
        depends on the crypt configuration; Google Drive itself is not made
        end-to-end encrypted by OAuth. File sizes, timing, and other metadata may
        remain visible to the storage provider.
      </p>
      <p>
        These informational pages do not request Drive access or receive OAuth
        tokens, encryption passwords, or Drive files. Read the{" "}
        <Link to="/oracle-rclone/privacy">Privacy Policy</Link> for data handling
        and access removal instructions.
      </p>
      <h2 className="small-title" id="terms">Terms of use</h2>
      <p>
        This application is for the owner's personal use only. The owner is
        responsible for account permissions, encryption keys, and backups.
        It is provided as is, without warranties. It is an independent personal
        project and is not affiliated with or endorsed by Google, Oracle, or
        the rclone project.
      </p>
      <p><a href="#terms">Terms of use</a> · <Link to="/contact">Contact the maintainer</Link></p>
    </Page>
  );
}

export function OracleRclonePrivacy() {
  return (
    <Page title="Privacy Policy">
      <p>Oracle Rclone · Last updated: November 3, 2026</p>
      <p>
        This policy covers Oracle Rclone, Yoseph Aljohani's private OAuth
        application for personal rclone access to Google Drive, and its
        informational pages.
      </p>
      <h2 className="small-title">Google data access and use</h2>
      <p>
        With the owner's consent, rclone accesses Drive file contents and metadata
        such as names, identifiers, sizes, and modification times within the
        granted scope. It uses this access only to carry out the owner's file
        transfer, backup, synchronization, and management commands. Depending on
        the granted scope, those commands may read, create, modify, or delete files.
      </p>
      <h2 className="small-title">Storage and protection</h2>
      <p>
        OAuth access and refresh tokens are retained in the owner's rclone
        configuration on the machine running rclone, not on these web pages.
        The owner controls that machine and is responsible for restricting access
        and protecting the configuration. Rclone configuration is not encrypted
        by default; file encryption through crypt does not itself encrypt tokens
        or the configuration file.
      </p>
      <p>
        Files uploaded through the configured crypt remote are stored encrypted
        in Google Drive. Encryption passwords and keys are managed by the owner.
        Downloaded or cached files and operational logs may remain on the owner's
        machine according to the commands and settings used. Encryption does not
        conceal all storage metadata.
      </p>
      <h2 className="small-title">Sharing and limited use</h2>
      <p>
        Data passes between Google Drive and the owner's rclone environment to
        perform the requested operations. Oracle Rclone does not sell Google
        user data, use it for advertising or AI model training, or share it with
        other people for independent use. Its use and transfer of information
        received from Google APIs will adhere to the{" "}
        <a href="https://developers.google.com/terms/api-services-user-data-policy">
          Google API Services User Data Policy
        </a>, including the Limited Use requirements.
      </p>
      <h2 className="small-title">Retention, deletion, and revocation</h2>
      <p>
        Drive files remain until the owner deletes them, subject to Google Drive's
        trash and retention behavior. Local copies and logs remain until removed
        by the owner. To stop access, remove Oracle Rclone from the{" "}
        <a href="https://myaccount.google.com/connections">Google Account connections</a>,
        then remove its tokens and remote configuration from every rclone machine.
        Revoking access does not delete existing Drive files or local copies;
        delete these separately, including any trash, caches, and backups as needed.
      </p>
      <h2 className="small-title">Website data and contact</h2>
      <p>
        These pages contain no Google sign-in or Drive data collection form.
        Website hosting and existing external resources may process ordinary
        request information, such as IP addresses and browser details. The site's
        theme preference is stored locally in the browser.
        For privacy questions, use the existing{" "}
        <Link to="/contact">contact page</Link>. Do not send credentials, tokens,
        encryption keys, or private files.
      </p>
      <p><Link to="/oracle-rclone">Back to Oracle Rclone</Link></p>
    </Page>
  );
}
